import { useCallback, useState } from "react";
import { useSearchParams } from "react-router-dom";

/** A product selection that can ARRIVE in the URL, and that stays there.
 *
 *  Dattatray: *"if I click on a vehicle drawer and then select spaces, I want
 *  to go into vehicle spaces! Not just spaces subpage!"* A link that lands you
 *  on an empty picker has not taken you anywhere — it has taken you to the
 *  place and made you say it again.
 *
 *  THIS DOES NOT BREAK THE NO-DEFAULT RULE, and the distinction is the whole
 *  reason this is a hook rather than a default value. *"dont select any
 *  dropdown option by default, let the user select"* is about arriving COLD:
 *  a picker that guesses makes every page below it read as broken, which is
 *  exactly what happened when it defaulted to `vehicle`. A `?kind=` in the URL
 *  is not a guess — it is a selection the person already made, one click ago,
 *  on a product they were looking at. With no parameter this returns `""` and
 *  nothing is selected.
 *
 *  THE SELECTION IS WRITTEN BACK, so the URL always describes what is on
 *  screen: the page becomes linkable and survives a refresh. `replace: true`
 *  because changing a dropdown is not a navigation — without it, Back would
 *  walk through every product the person tried instead of leaving the page.
 *
 *  ONE HOOK, FIVE PAGES. Spaces, Reference files, Tool updates, Permission map
 *  and the Products list all need the same behaviour, and CLAUDE.md is blunt
 *  about hand-building a second copy of something shared.
 */
export function useKindParam(param = "kind"): [string, (kind: string) => void] {
  const [sp, setSp] = useSearchParams();

  // Seeded ONCE from the URL. Reading `sp` on every render instead would make
  // the parameter authoritative, and then clearing the picker would fight the
  // URL that the clearing itself just rewrote.
  const [kind, setKindState] = useState(() => sp.get(param) ?? "");

  const setKind = useCallback((next: string) => {
    setKindState(next);
    setSp((prev) => {
      const out = new URLSearchParams(prev);
      if (next) out.set(param, next);
      else out.delete(param);
      return out;
    }, { replace: true });
  }, [param, setSp]);

  return [kind, setKind];
}
